// @ts-nocheck TODO

import {LitElement, PropertyValues, html, nothing} from "lit";
import {customElement, property, state} from "lit/decorators.js";
import {classMap} from "lit/directives/class-map.js";
import {repeat} from "lit/directives/repeat.js";

import {citationInformationStyles} from "./CitationInformation.styles.js";
import {citationFormats, cslNameTypes, cslUncommonNameTypes} from "../../../main/citation.js";
import Project from "../../../main/project.js";
import {STATES} from "./CitationPanel.js";
import Data from "../../../main/data/index.js";
import {citationPanelCreator, citationPanelData} from "./panel_utils.js";
import {SelectOption} from "../../common/controls-inputs/Select.js";

import "../../common/controls-inputs/Button.js";
import "../../common/controls-inputs/IconButton.js";
import "../../common/controls-inputs/LabeledTextInput.js";
import "../../common/CollapsibleSection.js";
import "./CitationEditor.js";

@customElement("pn-citation-information")
export class CitationEditor extends LitElement {
	@property() citation: citationPanelData = {
		approved:   false,
		structured: false,
		data:       {}
	};
	@property() editable:                    boolean;
	@property() isAutoCitationsDisabled:     boolean;
	@property() isRefreshAllowed:            boolean;
	@property() onClickCitationFormatOption: Function;
	@property() setStateSearch:              Function;
	@property() state:                       string;

	@state() _creators: citationPanelCreator[] = [];

	willUpdate(changedProperties: PropertyValues<this>) {
		if (changedProperties.has("citation")) {
			this._creators = (this.citation.creators ?? []).map((creator, i) => {
				return {...creator, id: i};
			});
		}
	}

	static styles = [citationInformationStyles];

	// TODO: do something, and what about the modal?
	onSelectCitationOption = this.citation.structured
		? this.onClickCitationFormatOption.bind(this)
		: () => {};

	onClickIdBtn = () => {
		const myEvent = new CustomEvent<{state: string}>("setCitationInformationState", {
			detail: {
				state: "search"
			},
			bubbles:  true,
			composed: true
		});

		this.dispatchEvent(myEvent);

		// TODO
		// sidebar.querySelector("#citation-search-field input").focus();
	};

	onClickSearchBackBtn = () => {
		const myEvent = new CustomEvent<{state: string}>("setCitationInformationState", {
			detail: {
				state: "initial"
			},
			bubbles:  true,
			composed: true
		});

		this.dispatchEvent(myEvent);
	};

	onClickAddContributor = () => {
		this._creators = [
			...this._creators,
			{
				creatorType: "author",
				given:       "",
				family:      "",
				id:          this.getNewContributorId()
			}
		];
	};

	getNewContributorId() {
		const ids = this._creators.map(creator => creator.id);
		return Math.max(...ids) + 1;
	}

	getCitationSelect() {
		// Default format options
		const options = citationFormats;

		// Format for this citation (or try to fall back to default)
		let render_format = this.citation.render_format ?? Project.getCitationStyle(Data.app.project);

		// See if the citation's render_format is in the default format list
		const selectedOption = options.find(option =>
			option.value === render_format
		);

		// If not (and it's truthy), add it to the list and select it
		if (!selectedOption && render_format) {
			const newOption = {
				label: render_format,
				value: render_format.replace(/[^a-zA-Z0-9]/g, "-") // spaces and weird characters break things
			};
			options.push(newOption);
			render_format = newOption.value;
		}

		return html`
			<pn-select
				.aria-labelledby="${"citation-format-label"}"
				.disabled="${!this.editable || !this.citation.structured}"
				id="citation-format-select"
				.options="${options}"
				.selectedOption=${selectedOption}
			>
			</pn-select>
		`;
	}

	getContributorSelectId(index: number) {
		return `citation-contributor-type-${index}`;
	}

	getContributorSelect(index: number, creatorType: string) {
		const id = this.getContributorSelectId(index);

		// Default to "Author" type
		let selectedOption = cslNameTypes[0] as SelectOption | undefined;

		// If the name type is not in the shorter array, add it to the select menu
		let extraOption = [] as SelectOption[];

		// Find the option object for this type
		selectedOption = cslNameTypes.find(nt => nt.value === creatorType);

		// If there was no matching option object in the frequently used
		// array, find it in the not frequently used name types array
		if (!selectedOption) {
			selectedOption = cslUncommonNameTypes.find(nt => nt.value === creatorType);
			extraOption = [selectedOption];
		}

		return html`
			<pn-select
				.aria-labelledby="${id.slice(0, id.length - 8)}"
				.disabled="${!this.editable}"
				.id="${id.slice(0, id.length - 8).concat("-select")}"
				.onSelectCallback="${(value: string) => {
					this._creators[index] = {
						...this._creators[index],
						creatorType: value
					};
				}}"
				.options="${[...cslNameTypes, ...extraOption]}"
				.selectedOption=${selectedOption}
			>
			</pn-select>
		`;
	}

	onClickDelete = (index: number) => {
		const remainingCreators = Array.from(this._creators);
		remainingCreators.splice(index, 1);

		this._creators = remainingCreators;
	};

	renderContributor(creator: citationPanelCreator, index: number) {
		return html`
			<div
				class="citation-contributor-container"
			>
				<div>
					<label
						class="citation-contributor-select-label is-bold"
						id="${this.getContributorSelectId(index)}"
					>
						Position
					</label>
					${this.getContributorSelect(index, creator.creatorType)}
				</div>

				<pn-labeled-text-input
					class="citation-contributor-given"
					.editable="${this.editable}"
					.isLabelBold=${true}
					label="First Name"
					.value="${creator.given}"
				></pn-labeled-text-input>

				<pn-labeled-text-input
					class="citation-contributor-family"
					.editable="${this.editable}"
					.isLabelBold=${true}
					label="Last Name"
					.value="${creator.family}"
				></pn-labeled-text-input>

				${this.editable && index !== 0
					? html`
						<pn-icon-button
							class="citation-contributor-delete"
							.tooltipText="${"Delete"}"
							icon="trash"
							.onClick="${() => this.onClickDelete(index)}"
						></<pn-icon-button>
					`
					: nothing
				}
			</div>
		`;
	}

	renderIndividualInputFields = () => {
		if (this.state !== STATES.STANDARD)
			return nothing;

		return html`
			<pn-collapsible-section
				class="${classMap({
					open: this.isAutoCitationsDisabled
				})}"
				.id="${"citation-individual-input-fields"}"
				.isOpenInitially="${false}"
				.title="${"Detailed Citation Information"}"
			>
				<pn-labeled-text-input
					.editable="${this.editable}"
					id="${"citation-field-title"}"
					.isLabelBold=${true}
					.label="${"Title"}"
					.required="${true}"
					.value="${this.citation.title}"
				></pn-labeled-text-input>

				<div id="${"citation-contributors-wrapper"}">
					<label class="is-bold">Contributor(s)</label>

					<div id="${"citation-contributors-list"}">
						${repeat(this._creators, (creator) => creator.id,
							(creator, index) => this.renderContributor(creator, index)
						)}
					</div>

					${this.editable
						? html`<pn-button
							.onClick="${this.onClickAddContributor}"
							renderType="link-blue"
						>
							Add another contributor
						</pn-button>`
						: nothing
					}
				</div>

				<pn-labeled-text-input
					.editable="${this.editable}"
					id="${"citation-field-publisher"}"
					.isLabelBold=${true}
					.label="${"Publisher"}"
					.value="${this.citation.publisher}"
				></pn-labeled-text-input>

				<div id="dates-container" class="two-column">
					<pn-labeled-text-input
						class="date-field"
						.editable="${this.editable}"
						id="${"citation-field-date-published"}"
						.isLabelBold=${true}
						.label="${"Date published"}"
						.required="${true}"
						.value="${this.citation.date_published}"
					></pn-labeled-text-input>

					<pn-labeled-text-input
						class="date-field"
						.editable="${this.editable}"
						id="${"citation-field-date-accessed"}"
						.isLabelBold=${true}
						.label="${"Date accessed"}"
						.value="${this.citation.date_accessed}"
					></pn-labeled-text-input>
				</div>
			</pn-collapsible-section>
		`;
	};

	render() {
		return html`
			<div
				id="citation-information"
				class="${classMap({
					"auto-citation-disabled": this.isAutoCitationsDisabled,
					[this.state]:             true
				})}"
			>
				${this.state === STATES.SEARCH
					? html`<pn-button
						className="${"dotted-focus-outline"}"
						id="citation-search-back-btn"
						@click="${this.onClickSearchBackBtn}"
						renderType="link"
					>
						<span part="left-arrow">&#8592;</span>Back
					</pn-button>`
					: nothing
				}

				${this.state !== STATES.UNSTRUCTURED
					? html`<pn-labeled-text-input
						.editable="${false}"
						.id="${"citation-field-url"}"
						.isLabelBold=${true}
						.label="${"URL"}"
						.value="${this.citation.url}"
					></pn-labeled-text-input>`
					: nothing
				}

				${this.editable && this.state !== STATES.SEARCH
					? html`<label class="is-bold" id="citation-identifier">
						<pn-button
							.onClick="${this.onClickIdBtn}"
							renderType="link-blue"
						>Click here</pn-button> to add or change the DOI, ISBN, or PMID.
					</label>`
					: nothing
				}

				<label class="is-bold" id="citation-format-label">
					Format *
				</label>
				${this.getCitationSelect()}

				${this.state === STATES.SEARCH
					? html`<pn-labeled-text-input
						.editable="${this.editable}"
						id="${"citation-search-field"}"
						.isLabelBold=${true}
						.label="${"DOI, ISBN, or PMID"}"
						.value=${this.citation.search_value}
					></pn-labeled-text-input>`
					: nothing
				}

				<pn-citation-editor
					.citation="${this.citation}"
					.editable="${this.editable}"
					.isAutoCitationsDisabled="${this.isAutoCitationsDisabled}"
					.isRefreshAllowed=${this.isRefreshAllowed}
					.state=${this.state}
				></pn-citation-editor>

				<pn-labeled-text-input
					.editable="${this.editable}"
					id="${"citation-field-pages"}"
					.isLabelBold=${true}
					.maxLength="${"32"}"
					.label="${"Page range (optional)"}"
					.value="${this.citation.pages}"
				></pn-labeled-text-input>

				${this.renderIndividualInputFields()}
			</div>
		`;
	}
};

declare global {
	interface HTMLElementTagNameMap {
		"pn-citation-information": CitationEditor;
	}
}
